// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  src/pages/Configuracoes/ApiKeys.tsx
//  Gerenciar chaves de API (X-API-Key): conectar Claude Desktop e integrações
//  ao Udisys sem renovar token de 24h. A chave em claro aparece uma única vez.
// ════════════════════════════════════════════════════════════════════════════

import { useCallback, useEffect, useState } from 'react';
import {
  KeyRound, Plus, Loader2, Copy, Check, Trash2, ShieldAlert, Inbox, RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';
import { apiKeysService, type ApiKeyResumo, type ApiKeyCriada } from '@/features/apikeys/apiKeysService';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';

function fmt(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('pt-BR');
}

export default function ApiKeys() {
  const [chaves, setChaves] = useState<ApiKeyResumo[]>([]);
  const [carregando, setCarregando] = useState(true);

  const [dialogAberto, setDialogAberto] = useState(false);
  const [nome, setNome] = useState('');
  const [expiraDias, setExpiraDias] = useState('');
  const [criando, setCriando] = useState(false);
  const [novaChave, setNovaChave] = useState<ApiKeyCriada | null>(null);
  const [copiado, setCopiado] = useState(false);
  const [revogando, setRevogando] = useState<number | null>(null);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      setChaves(await apiKeysService.listar());
    } catch (e) {
      toast.error((e as Error)?.message ?? 'Falha ao carregar as chaves.');
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => { void carregar(); }, [carregar]);

  function abrirDialog() {
    setNome(''); setExpiraDias(''); setNovaChave(null); setCopiado(false);
    setDialogAberto(true);
  }

  async function criar() {
    if (!nome.trim()) { toast.error('Dê um nome para a chave.'); return; }
    setCriando(true);
    try {
      const dias = expiraDias.trim() ? Number(expiraDias) : null;
      const criada = await apiKeysService.criar(nome.trim(), dias);
      setNovaChave(criada);        // troca o formulário pela exibição da chave
      await carregar();
    } catch (e) {
      toast.error((e as Error)?.message ?? 'Falha ao gerar a chave.');
    } finally {
      setCriando(false);
    }
  }

  async function copiar() {
    if (!novaChave) return;
    await navigator.clipboard.writeText(novaChave.chave);
    setCopiado(true);
    toast.success('Chave copiada.');
    setTimeout(() => setCopiado(false), 2000);
  }

  async function revogar(id: number) {
    setRevogando(id);
    try {
      await apiKeysService.revogar(id);
      toast.success('Chave revogada.');
      await carregar();
    } catch (e) {
      toast.error((e as Error)?.message ?? 'Falha ao revogar.');
    } finally {
      setRevogando(null);
    }
  }

  return (
    <div className="mx-auto max-w-4xl space-y-6 p-4">
      {/* Cabeçalho */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="rounded-lg bg-primary/10 p-2 text-primary"><KeyRound className="h-6 w-6" /></div>
          <div>
            <h1 className="text-xl font-bold">Chaves de API</h1>
            <p className="text-sm text-muted-foreground">
              Conecte o Claude Desktop e integrações ao Udisys — sem renovar token todo dia.
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="icon" onClick={() => void carregar()} disabled={carregando} title="Atualizar">
            <RefreshCw className={`h-4 w-4 ${carregando ? 'animate-spin' : ''}`} />
          </Button>
          <Button onClick={abrirDialog}><Plus className="mr-2 h-4 w-4" /> Nova chave</Button>
        </div>
      </div>

      {/* Aviso de segurança */}
      <div className="flex items-start gap-3 rounded-lg border border-amber-300/60 bg-amber-50/60 p-3 text-sm dark:bg-amber-950/20">
        <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
        <p className="text-muted-foreground">
          Uma chave dá o <b>mesmo acesso que o seu login</b>. Guarde como uma senha, não compartilhe,
          e <b>revogue</b> qualquer chave que não use mais. Ela é mostrada em claro só no momento da criação.
        </p>
      </div>

      {/* Lista */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Suas chaves</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {carregando && chaves.length === 0 && (
            <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Carregando…
            </div>
          )}

          {!carregando && chaves.length === 0 && (
            <div className="flex flex-col items-center gap-2 py-10 text-center text-sm text-muted-foreground">
              <Inbox className="h-6 w-6" />
              <p>Nenhuma chave ainda. Crie uma para conectar o Claude Desktop.</p>
            </div>
          )}

          {chaves.map((k) => (
            <div key={k.id} className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-md border p-3">
              <div className="min-w-[160px] flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-medium">{k.nome}</span>
                  {k.ativo
                    ? <Badge variant="outline" className="border-green-500/40 text-green-700 dark:text-green-400">ativa</Badge>
                    : <Badge variant="secondary">revogada</Badge>}
                </div>
                <code className="text-xs text-muted-foreground">{k.prefixo}</code>
              </div>
              <div className="grid grid-cols-2 gap-x-6 gap-y-0.5 text-xs text-muted-foreground sm:flex sm:gap-6">
                <span>Criada<br /><span className="text-foreground">{fmt(k.criadoEm)}</span></span>
                <span>Último uso<br /><span className="text-foreground">{fmt(k.ultimoUsoEm)}</span></span>
                <span>Expira<br /><span className="text-foreground">{k.expiraEm ? fmt(k.expiraEm) : 'nunca'}</span></span>
              </div>
              {k.ativo && (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive"
                      disabled={revogando === k.id}>
                      {revogando === k.id
                        ? <Loader2 className="h-4 w-4 animate-spin" />
                        : <Trash2 className="h-4 w-4" />}
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Revogar “{k.nome}”?</AlertDialogTitle>
                      <AlertDialogDescription>
                        Qualquer integração usando esta chave para de funcionar na hora. Não dá para desfazer.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancelar</AlertDialogCancel>
                      <AlertDialogAction
                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        onClick={() => void revogar(k.id)}>
                        Revogar
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Dialog: criar / mostrar a chave uma vez */}
      <Dialog open={dialogAberto} onOpenChange={(o) => { setDialogAberto(o); if (!o) setNovaChave(null); }}>
        <DialogContent className="sm:max-w-lg">
          {!novaChave ? (
            <>
              <DialogHeader>
                <DialogTitle>Nova chave de API</DialogTitle>
                <DialogDescription>
                  A chave herda as suas permissões. Você verá o valor completo só uma vez.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-2">
                <div className="space-y-2">
                  <Label htmlFor="ak-nome">Nome</Label>
                  <Input id="ak-nome" value={nome} onChange={(e) => setNome(e.target.value)}
                    placeholder="Ex.: Claude Desktop do financeiro" maxLength={60}
                    onKeyDown={(e) => { if (e.key === 'Enter') void criar(); }} autoFocus />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ak-exp">Expira em (dias) <span className="text-muted-foreground">— opcional</span></Label>
                  <Input id="ak-exp" type="number" min={1} value={expiraDias}
                    onChange={(e) => setExpiraDias(e.target.value)} placeholder="deixe vazio = não expira" />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setDialogAberto(false)}>Cancelar</Button>
                <Button onClick={() => void criar()} disabled={criando || !nome.trim()}>
                  {criando ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <KeyRound className="mr-2 h-4 w-4" />}
                  Gerar chave
                </Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Check className="h-5 w-5 text-green-600" /> Chave criada
                </DialogTitle>
                <DialogDescription>
                  Copie agora — por segurança ela <b>não será exibida novamente</b>.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3 py-2">
                <div className="flex items-center gap-2 rounded-md border bg-muted/60 p-2.5">
                  <code className="flex-1 break-all font-mono text-sm">{novaChave.chave}</code>
                  <Button size="sm" variant="secondary" onClick={() => void copiar()}>
                    {copiado ? <Check className="mr-1 h-4 w-4 text-green-600" /> : <Copy className="mr-1 h-4 w-4" />}
                    {copiado ? 'Copiado' : 'Copiar'}
                  </Button>
                </div>
                <Separator />
                <p className="text-xs text-muted-foreground">
                  Cole no cabeçalho <code className="font-mono">X-API-Key</code> da sua integração
                  (ou no <code className="font-mono">env</code> do Claude Desktop, conforme o guia de conexão).
                </p>
              </div>
              <DialogFooter>
                <Button onClick={() => { setDialogAberto(false); setNovaChave(null); }}>Concluído</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
